/**
 * Shared actor display-name resolver.
 *
 * Audit entries store `actorUsername` as whatever the WS authorizer stamped on
 * the connection row at the time of the mutation. When a Cognito pool doesn't
 * have a username alias configured, `cognito:username` is the raw UUID sub —
 * so older rows (and, until the authorizer fix, brand-new ones too) carry the
 * sub as the "username". The audit dashboard, the player-side Activity
 * section, and the Sheet session log all need to turn that UUID back into a
 * human name, and they all want the same resolution order.
 *
 * Resolution order for a UUID-looking username:
 *   1. Character roster — if any bundle's ownerSub matches, prefer the
 *      character profile name (PII-safe across players). Fall back to the
 *      email LOCAL PART of `ownerUsername` if there's no profile name yet
 *      (e.g. a freshly-created character with no name). Never expose the
 *      full email — the @domain is PII the table shouldn't see.
 *   2. Admin user list — if present (DM-only), match on sub and use the
 *      email local part for brevity ("tyebryant98@hotmail.com" → "tyebryant98").
 *   3. Connected-players list — sub → username reported on the status row.
 *   4. First 8 UUID chars + ellipsis — last-ditch so the column isn't empty.
 *
 * Non-UUID inputs are passed through unchanged UNLESS they look like an email
 * — bare emails get the @domain stripped so accidental email-as-username
 * leaks (the most common shape; ownerUsername is set to the player's email at
 * assignment time) don't expose PII to other players. DMs who legitimately
 * need full email addresses should look at the admin user list directly, not
 * at rendered actor names.
 */

/** UUID v4 pattern — matches Cognito subs. */
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Minimal email-shaped pattern: something@something.something. Deliberately
 * loose — we only use it to decide whether to strip a @domain before display,
 * not to validate addresses.
 */
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Strip the @domain off an email-shaped string for safer display to other
 * players (drops PII like the email provider). Pass-through for non-emails.
 *
 * Examples:
 *   "tyebryant98@hotmail.com" → "tyebryant98"
 *   "Saru"                    → "Saru"
 *   ""                        → ""
 */
export function stripEmailDomain(s: string | null | undefined): string {
  if (!s) return '';
  if (!EMAIL_RE.test(s)) return s;
  const at = s.indexOf('@');
  return at > 0 ? s.slice(0, at) : s;
}

/** Minimal roster entry shape — narrower than CharacterBundle so non-TS callers don't need the full type. */
export interface DisplayNameRosterEntry {
  ownerSub?: string | null;
  ownerUsername?: string | null;
  profile?: { name?: string } | null;
}

/** Admin users list entry — matches AdminUser from adminApi.ts. DM-only. */
export interface DisplayNameAdminUser {
  sub: string;
  email: string | null;
}

/** Connected player entry — matches SessionStatusResponse.connectedPlayers[]. */
export interface DisplayNameConnectedPlayer {
  sub: string;
  username: string;
}

export interface DisplayNameSources {
  /** Character roster, always available on Party/Sheet/Audit pages. */
  roster?: DisplayNameRosterEntry[];
  /** Admin user list — only populated when the current viewer is a DM (listUsers is admin-only). */
  adminUsers?: DisplayNameAdminUser[];
  /** Currently-connected players from session status — useful when a DM isn't a character owner. */
  connectedPlayers?: DisplayNameConnectedPlayer[];
}

export function isCognitoSub(username: string): boolean {
  return UUID_RE.test(username);
}

/**
 * Resolve an `actorUsername` value into something friendly to show to a
 * human. Non-UUID inputs are assumed to already be display-ready.
 */
export function resolveActorDisplayName(
  username: string,
  sources: DisplayNameSources = {},
): string {
  if (!username) return username;
  // Non-UUID input: still strip @domain if it's an email-shaped value, so a
  // bare `actorUsername: "tyebryant98@hotmail.com"` doesn't leak the address
  // to other players just by skipping the sub lookup. Character names and
  // arbitrary usernames pass through unchanged.
  if (!isCognitoSub(username)) return stripEmailDomain(username);

  const { roster, adminUsers, connectedPlayers } = sources;

  if (roster) {
    for (const b of roster) {
      if (b.ownerSub === username) {
        // Prefer the character profile name — it's the public-facing handle
        // and doesn't leak the owner's email to the rest of the table.
        const charName = b.profile?.name;
        if (charName) return charName;
        // Fall back to ownerUsername with the @domain stripped, so a
        // freshly-imported character with no profile name yet doesn't blank
        // out (and doesn't leak the full email either).
        if (b.ownerUsername) return stripEmailDomain(b.ownerUsername);
      }
    }
  }

  if (adminUsers) {
    for (const u of adminUsers) {
      if (u.sub === username && u.email) {
        // Strip @domain for brevity in dense tables.
        return u.email.split('@')[0];
      }
    }
  }

  if (connectedPlayers) {
    for (const p of connectedPlayers) {
      if (p.sub === username && p.username) {
        // Connected-players `username` is whatever the connect lambda stored;
        // skip it if that's also just the sub.
        if (!isCognitoSub(p.username)) return stripEmailDomain(p.username);
      }
    }
  }

  // Last-ditch: 8-char prefix so the column still has *something*.
  return username.slice(0, 8) + '…';
}
